import { useEffect, useState } from 'react';
import { serverNow } from '../time-sync';
import { ClockDisplay, Digit, Well } from '../channels/scoreboard/digits';
import { useSide, useWidgetData } from './context';
import { useSize } from './useSize';
import type { WidgetBinding } from './types';
import { ensureKeyframes } from '../effects/keyframes';
import { usePlayerStatsRows } from './usePlayerStatsRows';
import type { PlayerLine } from './playerLine';

// Every widget fills its zone (100% × 100%), centered, and scales its
// content to the measured height so a template can place it anywhere.

function Fill({
  children,
  innerRef,
}: {
  children: React.ReactNode;
  innerRef?: React.Ref<HTMLDivElement>;
}) {
  return (
    <div
      ref={innerRef}
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      {children}
    </div>
  );
}

function Label({ text, color }: { text: string; color: string }) {
  return (
    <div
      style={{
        fontSize: 'clamp(8px, 1.6vh, 16px)',
        fontWeight: 800,
        letterSpacing: 2,
        color,
        marginBottom: 4,
        textAlign: 'center',
      }}
    >
      {text}
    </div>
  );
}

// ─── Score ──────────────────────────────────────────────────────────

function ScoreWidget({ binding }: { binding: WidgetBinding }) {
  const side = binding.side ?? 'home';
  const { roster, side: s } = useSide(side);
  const { theme, mode, isSample } = useWidgetData();
  const { ref, height } = useSize<HTMLDivElement>();
  const score = s?.score ?? (isSample ? (side === 'home' ? 102 : 98) : 0);
  const h = Math.max(20, Math.min(height * 0.62, height));
  const name =
    roster?.name?.toUpperCase() ?? (side === 'home' ? 'HOME' : 'AWAY');
  return (
    <Fill innerRef={ref}>
      <Label text={name} color={theme.lbl} />
      <Well pad="6px 10px">
        <Digit
          val={score}
          digits={score >= 100 ? 3 : 2}
          color="#ff1a1a"
          h={h}
          mode={mode}
        />
      </Well>
    </Fill>
  );
}

// ─── Game clock ─────────────────────────────────────────────────────

function useLiveClock() {
  const { state } = useWidgetData();
  const c = state?.clock;
  const [, force] = useState(0);
  useEffect(() => {
    if (!c?.running) return;
    const id = setInterval(() => force((x) => x + 1), 250);
    return () => clearInterval(id);
  }, [c?.running, c?.startedAt]);
  if (!c) return { m: 0, s: 0, running: false };
  if (!c.running || !c.startedAt) return { m: c.m, s: c.s, running: false };
  const elapsed = Math.max(
    0,
    Math.floor((serverNow() - new Date(c.startedAt).getTime()) / 1000),
  );
  const total = Math.max(0, c.m * 60 + c.s - elapsed);
  return { m: Math.floor(total / 60), s: total % 60, running: true };
}

function GameClockWidget() {
  const { mode, theme } = useWidgetData();
  const live = useLiveClock();
  const { ref, height } = useSize<HTMLDivElement>();
  const h = Math.max(20, Math.min(height * 0.62, height));
  return (
    <Fill innerRef={ref}>
      <Label text="GAME CLOCK" color={theme.sub} />
      <Well pad="6px 12px">
        <ClockDisplay
          m={live.m}
          s={live.s}
          color={live.running ? '#22c55e' : '#fff'}
          h={h}
          mode={mode}
        />
      </Well>
    </Fill>
  );
}

// ─── Shot clock ─────────────────────────────────────────────────────

function ShotClockWidget() {
  const { state, mode } = useWidgetData();
  const ss = (state?.sportState ?? {}) as Record<string, unknown>;
  const raw = typeof ss.shotClock === 'number' ? (ss.shotClock as number) : 24;
  const running = ss.shotClockRunning === true;
  const startedAt =
    typeof ss.shotClockStartedAt === 'string'
      ? (ss.shotClockStartedAt as string)
      : null;
  const [, force] = useState(0);
  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => force((x) => x + 1), 250);
    return () => clearInterval(id);
  }, [running, startedAt]);
  const live =
    running && startedAt
      ? Math.max(
          0,
          raw -
            Math.floor((serverNow() - new Date(startedAt).getTime()) / 1000),
        )
      : raw;
  const { ref, height } = useSize<HTMLDivElement>();
  const h = Math.max(16, Math.min(height * 0.55, height));
  return (
    <Fill innerRef={ref}>
      <Label text="SHOT CLOCK" color="#fbbf24" />
      <Well pad="6px 10px">
        <Digit
          val={live}
          digits={2}
          color={live <= 5 ? '#ff1a1a' : '#fbbf24'}
          h={h}
          mode={mode}
        />
      </Well>
    </Fill>
  );
}

// ─── Period ─────────────────────────────────────────────────────────

function PeriodWidget({ binding }: { binding: WidgetBinding }) {
  const { state, mode, theme } = useWidgetData();
  const labelType = (binding.options?.labelType as string) ?? 'PERIOD';
  const { ref, height } = useSize<HTMLDivElement>();
  const h = Math.max(16, Math.min(height * 0.55, height));
  const halftime = state?.intermission === 'halftime';
  return (
    <Fill innerRef={ref}>
      <Label text={halftime ? 'HALF' : labelType} color={theme.sub} />
      {halftime ? (
        <div
          style={{
            fontSize: 'clamp(16px, 5vh, 48px)',
            fontWeight: 900,
            color: '#fbbf24',
            letterSpacing: 4,
          }}
        >
          HT
        </div>
      ) : (
        <Well pad="4px 10px">
          <Digit
            val={state?.period ?? 1}
            digits={1}
            color="#ff1a1a"
            h={h}
            mode={mode}
          />
        </Well>
      )}
    </Fill>
  );
}

// ─── Team name ──────────────────────────────────────────────────────

function TeamNameWidget({ binding }: { binding: WidgetBinding }) {
  const side = binding.side ?? 'home';
  const { roster } = useSide(side);
  const { theme, isSample } = useWidgetData();
  const name =
    roster?.name?.toUpperCase() ??
    (isSample ? (side === 'home' ? 'WILDCATS' : 'HAWKS') : side.toUpperCase());
  const color = roster?.colorPrimary ?? theme.lbl;
  return (
    <Fill>
      <div
        style={{
          fontSize: 'clamp(14px, 4vh, 44px)',
          fontWeight: 900,
          color: theme.lbl,
          letterSpacing: 1,
          textAlign: 'center',
          lineHeight: 1.05,
        }}
      >
        {name}
      </div>
      <div
        style={{
          marginTop: 6,
          width: '40%',
          height: 5,
          borderRadius: 3,
          background: color,
        }}
      />
    </Fill>
  );
}

// ─── Possession ─────────────────────────────────────────────────────

function PossessionWidget({ binding }: { binding: WidgetBinding }) {
  const side = binding.side ?? 'home';
  const { state, theme } = useWidgetData();
  const active = state?.possession === side;
  return (
    <Fill>
      <div
        style={{
          fontSize: 'clamp(12px, 2.4vh, 22px)',
          fontWeight: 900,
          letterSpacing: 3,
          color: active ? '#ff1a1a' : theme.sub,
          opacity: active ? 1 : 0.35,
          textShadow: active ? '0 0 10px #ff1a1aaa' : 'none',
        }}
      >
        {side === 'home' ? '◀ ' : ''}POSS{side === 'away' ? ' ▶' : ''}
      </div>
    </Fill>
  );
}

// ─── Bonus ──────────────────────────────────────────────────────────

function BonusWidget({ binding }: { binding: WidgetBinding }) {
  const side = binding.side ?? 'home';
  const { side: s } = useSide(side);
  const double = s?.doubleBonus === true;
  const on = s?.bonus === true || double;
  const color = double ? '#ff4d4d' : on ? '#fbbf24' : '#3a3a30';
  return (
    <Fill>
      <div
        style={{
          fontSize: 'clamp(10px, 1.8vh, 18px)',
          fontWeight: 900,
          letterSpacing: 2,
          color,
          border: `2px solid ${on ? color : 'transparent'}`,
          borderRadius: 6,
          padding: '4px 10px',
          background: on ? `${color}22` : 'transparent',
        }}
      >
        {double ? 'DBL BONUS' : 'BONUS'}
      </div>
    </Fill>
  );
}

// ─── Timeouts ───────────────────────────────────────────────────────

function TimeoutsWidget({ binding }: { binding: WidgetBinding }) {
  const side = binding.side ?? 'home';
  const { side: s } = useSide(side);
  const { mode, theme } = useWidgetData();
  const { ref, height } = useSize<HTMLDivElement>();
  const h = Math.max(14, Math.min(height * 0.5, 60));
  return (
    <Fill innerRef={ref}>
      <Label text="TOL" color={theme.sub} />
      <Well pad="3px 6px">
        <Digit
          val={s?.timeoutsRemaining ?? 0}
          digits={1}
          color="#22c55e"
          h={h}
          mode={mode}
        />
      </Well>
    </Fill>
  );
}

// ─── Team fouls ─────────────────────────────────────────────────────

function TeamFoulsWidget({ binding }: { binding: WidgetBinding }) {
  const side = binding.side ?? 'home';
  const { side: s } = useSide(side);
  const { mode } = useWidgetData();
  const { ref, height } = useSize<HTMLDivElement>();
  const h = Math.max(14, Math.min(height * 0.5, 60));
  return (
    <Fill innerRef={ref}>
      <Label text="FOULS" color="#ff6666" />
      <Well pad="3px 6px">
        <Digit
          val={s?.fouls ?? 0}
          digits={1}
          color="#ff1a1a"
          h={h}
          mode={mode}
        />
      </Well>
    </Fill>
  );
}

// ─── Player stats table ─────────────────────────────────────────────

const SAMPLE_PLAYERS: PlayerLine[] = [
  { no: '2', name: 'JOHNSON', f: 3, pts: 28, reb: 0, ast: 0 },
  { no: '15', name: 'WILLIAMS', f: 4, pts: 22, reb: 0, ast: 0 },
  { no: '25', name: 'CARTER', f: 1, pts: 18, reb: 0, ast: 0 },
  { no: '11', name: 'ROBINSON', f: 2, pts: 21, reb: 0, ast: 0 },
  { no: '22', name: 'DAVIS', f: 4, pts: 13, reb: 0, ast: 0 },
];

function PlayerStatsTableWidget({ binding }: { binding: WidgetBinding }) {
  const side = binding.side ?? 'home';
  const { snapshot, state, theme, isSample } = useWidgetData();

  useEffect(() => {
    ensureKeyframes();
  }, []);

  const { rows, flashPlayerId, flashColor } = usePlayerStatsRows(side, snapshot, state, isSample, SAMPLE_PLAYERS);

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        background: theme.ac,
        borderRadius: 6,
        border: `2px solid ${theme.br}`,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '14% 1fr 14% 18%',
          padding: '4px 8px',
          background: theme.bg,
          borderBottom: `1px solid ${theme.br}`,
          fontSize: 'clamp(7px, 1.2vh, 11px)',
          fontWeight: 800,
          letterSpacing: 1,
          color: theme.sub,
        }}
      >
        <span>NO</span>
        <span>PLAYER</span>
        <span style={{ textAlign: 'center' }}>F</span>
        <span style={{ textAlign: 'center' }}>PTS</span>
      </div>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {rows.map(({ id, line: p }, i) => {
          const flashing = !!id && flashPlayerId === id;
          return (
          <div
            key={id || i}
            data-player-id={id}
            style={{
              display: 'grid',
              gridTemplateColumns: '14% 1fr 14% 18%',
              alignItems: 'center',
              flex: 1,
              padding: '0 8px',
              background: i % 2 === 0 ? theme.ac : theme.bg,
              borderBottom: `1px solid ${theme.br}`,
              fontSize: 'clamp(9px, 1.8vh, 18px)',
              fontWeight: 700,
              color: theme.lbl,
              ['--row-flash' as string]: flashColor,
              animation: flashing ? 'slPlayerFlash 1.6s ease-out' : undefined,
            } as React.CSSProperties}
          >
            <span style={{ fontFamily: 'ui-monospace, monospace', color: '#ff6666' }}>
              {p.no}
            </span>
            <span
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {p.name}
            </span>
            <span
              style={{
                textAlign: 'center',
                fontFamily: 'ui-monospace, monospace',
                color: p.f >= 4 ? '#fbbf24' : '#ff1a1a',
              }}
            >
              {p.f}
            </span>
            <span
              style={{
                textAlign: 'center',
                fontFamily: 'ui-monospace, monospace',
                color: '#22c55e',
              }}
            >
              {p.pts}
            </span>
          </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Team logo (abbreviation disc) ──────────────────────────────────

function TeamLogoWidget({ binding }: { binding: WidgetBinding }) {
  const side = binding.side ?? 'home';
  const { roster } = useSide(side);
  const { isSample } = useWidgetData();
  const abbr =
    roster?.abbreviation ??
    roster?.name?.slice(0, 3).toUpperCase() ??
    (isSample ? (side === 'home' ? 'WLD' : 'HWK') : side.slice(0, 3).toUpperCase());
  const color =
    roster?.colorPrimary ?? (side === 'home' ? '#dc2626' : '#1d4ed8');
  const logoUrl = roster?.logoUrl ?? null;
  if (logoUrl) {
    return (
      <div style={{ width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center' }}>
        <img src={logoUrl} alt={roster?.abbreviation ?? ''} style={{ maxWidth:'100%', maxHeight:'100%', objectFit:'contain' }} />
      </div>
    );
  }
  return (
    <Fill>
      <div
        style={{
          width: 'min(70%, 70cqh)',
          aspectRatio: '1 / 1',
          borderRadius: '50%',
          background: color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 900,
          fontSize: 'clamp(14px, 4vh, 40px)',
          color: '#fff',
          letterSpacing: 1,
          boxShadow: `0 0 24px ${color}66`,
        }}
      >
        {abbr}
      </div>
    </Fill>
  );
}

// ─── Team stats summary ─────────────────────────────────────────────

function pct(m: number, a: number): string {
  return a > 0 ? `${Math.round((m / a) * 100)}%` : '—';
}

function TeamStatsSummaryWidget({ binding }: { binding: WidgetBinding }) {
  const side = binding.side ?? 'home';
  const { snapshot, state, theme } = useWidgetData();
  const roster = snapshot?.rosters[side];
  const stats = state?.playerStats ?? {};
  const t: Record<string, number> = {};
  for (const p of roster?.players ?? []) {
    const s = stats[p.id] ?? {};
    for (const k of ['fgm', 'fga', 'tpm', 'tpa', 'ftm', 'fta', 'reb', 'ast', 'to']) {
      t[k] = (t[k] ?? 0) + (s[k] ?? 0);
    }
  }
  const cells: Array<[string, string]> = [
    ['FG', pct(t.fgm ?? 0, t.fga ?? 0)],
    ['3FG', pct(t.tpm ?? 0, t.tpa ?? 0)],
    ['FT', pct(t.ftm ?? 0, t.fta ?? 0)],
    ['REB', String(t.reb ?? 0)],
    ['AST', String(t.ast ?? 0)],
    ['TO', String(t.to ?? 0)],
  ];
  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        alignContent: 'center',
        gap: 4,
        color: theme.lbl,
      }}
    >
      {cells.map(([k, v]) => (
        <div key={k} style={{ textAlign: 'center' }}>
          <div
            style={{
              fontSize: 'clamp(8px, 1.4vh, 13px)',
              fontWeight: 800,
              color: theme.sub,
              letterSpacing: 1,
            }}
          >
            {k}
          </div>
          <div
            style={{
              fontFamily: 'ui-monospace, monospace',
              fontSize: 'clamp(11px, 2.2vh, 22px)',
              fontWeight: 800,
            }}
          >
            {v}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Message banner ─────────────────────────────────────────────────

function MessageBannerWidget({ binding }: { binding: WidgetBinding }) {
  const { theme } = useWidgetData();
  const text = (binding.options?.text as string) ?? '';
  return (
    <Fill>
      <div
        style={{
          fontSize: 'clamp(12px, 2.4vh, 24px)',
          fontWeight: 800,
          letterSpacing: 3,
          color: theme.sub,
          textAlign: 'center',
        }}
      >
        {text}
      </div>
    </Fill>
  );
}

// ─── Media zone ─────────────────────────────────────────────────────

function MediaZoneWidget({ binding }: { binding: WidgetBinding }) {
  const { snapshot, theme, isSample } = useWidgetData();
  const images = (binding.options?.images as string[] | undefined) ?? [];
  const rotation = (binding.options?.rotationSec as number | undefined) ?? 8;
  const [i, setI] = useState(0);
  useEffect(() => {
    if (images.length < 2) return;
    const id = setInterval(
      () => setI((x) => (x + 1) % images.length),
      rotation * 1000,
    );
    return () => clearInterval(id);
  }, [images.length, rotation]);

  if (images.length > 0) {
    return (
      <div style={{ width: '100%', height: '100%', overflow: 'hidden', borderRadius: 8 }}>
        <img
          src={images[i]}
          alt=""
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      </div>
    );
  }

  // Placeholder: matchup graphic.
  const home = snapshot?.rosters.home.name ?? (isSample ? 'WILDCATS' : 'HOME');
  const away = snapshot?.rosters.away.name ?? (isSample ? 'HAWKS' : 'AWAY');
  const hc = snapshot?.rosters.home.colorPrimary ?? '#dc2626';
  const ac = snapshot?.rosters.away.colorPrimary ?? '#1d4ed8';
  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        borderRadius: 8,
        background: `linear-gradient(135deg, ${hc}33, ${ac}33)`,
        border: `2px solid ${theme.br}`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        color: theme.lbl,
      }}
    >
      <div style={{ fontSize: 'clamp(18px, 5vh, 64px)', fontWeight: 900, letterSpacing: 2 }}>
        {home}
      </div>
      <div style={{ fontSize: 'clamp(12px, 2.4vh, 24px)', fontWeight: 700, color: theme.sub }}>
        vs
      </div>
      <div style={{ fontSize: 'clamp(18px, 5vh, 64px)', fontWeight: 900, letterSpacing: 2 }}>
        {away}
      </div>
    </div>
  );
}

// ─── Sponsor banner (rotating) ──────────────────────────────────────

function SponsorBannerWidget({ binding }: { binding: WidgetBinding }) {
  const { theme } = useWidgetData();
  const sponsors =
    (binding.options?.sponsors as Array<{ name: string; tagline?: string }>) ??
    [
      { name: "Tony's Pizza", tagline: '2-for-1 game-night slices' },
      { name: 'Riverside Auto', tagline: 'Drive home a winner' },
      { name: 'Bright Smiles Ortho', tagline: 'Cheer with confidence' },
    ];
  const rotation = (binding.options?.rotationSec as number | undefined) ?? 6;
  const [i, setI] = useState(0);
  useEffect(() => {
    if (sponsors.length < 2) return;
    const id = setInterval(
      () => setI((x) => (x + 1) % sponsors.length),
      rotation * 1000,
    );
    return () => clearInterval(id);
  }, [sponsors.length, rotation]);
  const s = sponsors[i] ?? sponsors[0];
  return (
    <Fill>
      <div
        style={{
          fontSize: 'clamp(14px, 3vh, 30px)',
          fontWeight: 900,
          letterSpacing: 3,
          color: '#e8c84a',
          textShadow: '0 0 10px rgba(232,200,74,0.3)',
          textAlign: 'center',
        }}
      >
        {s?.name}
      </div>
      {s?.tagline && (
        <div
          style={{
            fontSize: 'clamp(9px, 1.6vh, 15px)',
            color: theme.sub,
            letterSpacing: 2,
            marginTop: 3,
            textAlign: 'center',
          }}
        >
          {s.tagline}
        </div>
      )}
    </Fill>
  );
}

// ─── Venue name ─────────────────────────────────────────────────────

function VenueNameWidget() {
  const { snapshot, theme, isSample } = useWidgetData();
  const name = isSample ? 'RIVERSIDE FIELDHOUSE' : (snapshot?.venueName ?? '');
  if (!name) return null;
  return (
    <div style={{ width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center',
      color: theme.sub, fontWeight:800, letterSpacing:3, textTransform:'uppercase',
      fontSize:'clamp(8px,2.4vh,22px)', overflow:'hidden', whiteSpace:'nowrap' }}>{name}</div>
  );
}

// ─── Win probability ────────────────────────────────────────────────

function WinProbabilityWidget() {
  const { snapshot, state, theme, isSample } = useWidgetData();
  const p = isSample ? 58 : Math.max(0, Math.min(100, state?.winProbHome ?? 50));
  const home = snapshot?.rosters.home, away = snapshot?.rosters.away;
  const hc = home?.colorPrimary ?? '#2563eb', ac = away?.colorPrimary ?? '#c0392b';
  const habbr = home?.abbreviation ?? 'HOME', aabbr = away?.abbreviation ?? 'AWAY';
  return (
    <div style={{ width:'100%', height:'100%', display:'flex', flexDirection:'column', justifyContent:'center', gap:'0.4em', color: theme.lbl, fontWeight:700 }}>
      <div style={{ display:'flex', justifyContent:'space-between', fontSize:'clamp(6px,1.4vh,11px)', letterSpacing:1 }}>
        <span>{habbr} {p}%</span><span style={{ color: theme.sub }}>WIN PROBABILITY</span><span>{100 - p}% {aabbr}</span>
      </div>
      <div style={{ height:'0.5em', minHeight:4, borderRadius:999, overflow:'hidden', display:'flex', background: ac }}>
        <div style={{ width:`${p}%`, background: hc, transition:'width 0.6s ease' }} />
      </div>
    </div>
  );
}

// ─── Scoring run ────────────────────────────────────────────────────

function ScoringRunWidget() {
  const { snapshot, state, isSample } = useWidgetData();
  const run = isSample ? { side:'home' as const, count:8 } : state?.scoringRun;
  if (!run || run.count < 4) return null;
  const color = snapshot?.rosters[run.side]?.colorPrimary ?? '#ff7a00';
  return (
    <div style={{ width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center',
      color, fontWeight:800, letterSpacing:1, fontSize:'clamp(8px,2vh,16px)' }}>🔥 {run.count}-0 RUN</div>
  );
}

// ─── Dispatch ───────────────────────────────────────────────────────

export function WidgetHost({ binding }: { binding: WidgetBinding }) {
  switch (binding.kind) {
    case 'score':
      return <ScoreWidget binding={binding} />;
    case 'gameClock':
      return <GameClockWidget />;
    case 'shotClock':
      return <ShotClockWidget />;
    case 'period':
      return <PeriodWidget binding={binding} />;
    case 'teamName':
      return <TeamNameWidget binding={binding} />;
    case 'possession':
      return <PossessionWidget binding={binding} />;
    case 'bonus':
      return <BonusWidget binding={binding} />;
    case 'timeouts':
      return <TimeoutsWidget binding={binding} />;
    case 'teamFouls':
      return <TeamFoulsWidget binding={binding} />;
    case 'playerStatsTable':
      return <PlayerStatsTableWidget binding={binding} />;
    case 'teamLogo':
      return <TeamLogoWidget binding={binding} />;
    case 'teamStatsSummary':
      return <TeamStatsSummaryWidget binding={binding} />;
    case 'mediaZone':
      return <MediaZoneWidget binding={binding} />;
    case 'sponsorBanner':
      return <SponsorBannerWidget binding={binding} />;
    case 'messageBanner':
      return <MessageBannerWidget binding={binding} />;
    case 'venueName':
      return <VenueNameWidget />;
    case 'winProbability':
      return <WinProbabilityWidget />;
    case 'scoringRun':
      return <ScoringRunWidget />;
    default:
      return null;
  }
}
